import { cloneDeep } from "lodash";
import { isBlank } from "@/utils/String";
import { SYSTEM_RESPONSE_CODE } from "@/views/common/enums";
import {
  TM_EXPORT_OPTIONS,
  TB_EXPORT_OPTIONS,
  ExportValidator,
  ExportValidationError,
} from "@/views/components/data";

const STAGE_NORMAL_DESC = {
  parsing: "文件解析中",
  parsed: "文件解析完成",
  aligning: "自动句对齐中",
  aligned: "已自动句对齐",
  extractingTerm: "自动提取术语中",
  extractDone: "已提取术语",
};

const STAGE_ERROR_DESC = {
  parseError: "文件解析失败",
  alignError: "自动句对齐失败",
  extractError: "提取术语失败",
};

const SECONDARY_STAGE_DESC = {
  upload: "上传文件",
  export: "导出文件",
  tm: "生成记忆库",
};

const STAGE_TIP_DATA = {
  upload: {
    text: SECONDARY_STAGE_DESC.upload,
    extra: {
      style: {
        color: "#909399",
      },
    },
  },
  parsing: {
    text: STAGE_NORMAL_DESC.parsing,
  },
  parsed: {
    text: STAGE_NORMAL_DESC.parsed,
    extra: {
      style: {
        color: "#409EFF",
      },
    },
  },
  aligning: {
    text: STAGE_NORMAL_DESC.aligning,
  },
  aligned: {
    text: STAGE_NORMAL_DESC.aligned,
    extra: {
      style: {
        color: "#67C23A",
      },
      secondaryTip: "可手动调整句子",
    },
  },
  extracting: {
    text: STAGE_NORMAL_DESC.extractingTerm,
    branches: [{ text: SECONDARY_STAGE_DESC.export }],
  },
  extractDone: {
    text: STAGE_NORMAL_DESC.extractDone,
    extra: {
      style: {
        color: "#E6A23C",
      },
      secondaryTip: "可手动筛选术语",
    },
  },
  exportTerm: {
    text: SECONDARY_STAGE_DESC.export,
  },
};

const LAST_STAGE_TIP_DATA = {
  export: {
    text: SECONDARY_STAGE_DESC.export,
    extra: {
      style: {
        arrow: {
          transform: "rotate(-45deg)",
        },
        text: {
          color: "#909399",
          position: "relative",
          top: "-12px",
        },
      },
    },
  },
  tm: {
    text: SECONDARY_STAGE_DESC.tm,
    extra: {
      style: {
        arrow: {
          transform: "rotate(45deg)",
        },
        text: {
          color: "#909399",
          position: "relative",
          top: "12px",
        },
      },
    },
  },
};

const ALIGNMENT_TASK_STATE = {
  ready: 0,
  parsing: 1,
  parsed: 2,
  parseError: 3,
  aligning: 4,
  aligned: 5,
  alignError: 6,
  extractingTerm: 7,
  extractDone: 8,
  extractError: 9,
};

const ALIGNMENT_TASK_ERROR = {
  parseError: 3,
  alignError: 6
};

const ALIGNMENT_TASK_NORMAL = {
  parsed: 2,
  aligned: 5,
};

class AlignmentCellCheckerBuilder {
  nonEmptyEnabled = false;
  maxLengthEnabled = false;

  withAllEnabled() {
    this.nonEmptyEnabled = true;
    this.maxLengthEnabled = true;
    return this;
  }

  withNonEmptyEnabled() {
    this.nonEmptyEnabled = true;
    return this;
  }

  withMaxLengthEnabled() {
    this.maxLengthEnabled = true;
    return this;
  }

  build() {
    return new AlignmentCellChecker(
      this.nonEmptyEnabled,
      this.maxLengthEnabled
    );
  }
}

class AlignmentCellChecker {
  static MAX_CELL_LENGTH = 20000;

  constructor(nonEmptyEnabled, maxLengthEnabled) {
    this.nonEmptyEnabled = nonEmptyEnabled;
    this.maxLengthEnabled = maxLengthEnabled;
  }

  check(text) {
    const length = text ? text.length : 0;
    if (this.nonEmptyEnabled && length === 0) {
      return {
        valid: false,
        message: "单元格不能为空",
      };
    }
    if (!this.maxLengthEnabled)
      return {
        valid: true,
        message: null,
      };
    if (length > AlignmentCellChecker.MAX_CELL_LENGTH) {
      return {
        valid: false,
        message: `长度不能超过${AlignmentCellChecker.MAX_CELL_LENGTH}字符`,
      };
    }
    return {
      valid: true,
      message: null,
    };
  }
}

const ALIGNMENT_TASK_OPTION = {
  parse: {
    waiting: {
      state: [ALIGNMENT_TASK_STATE.parsing],
      msg: "正在解析文档，请稍候...",
    },
    expect: {
      state: [
        ALIGNMENT_TASK_STATE.parsed,
        ALIGNMENT_TASK_STATE.aligning,
        ALIGNMENT_TASK_STATE.aligned,
      ],
      msg: "文档解析完成",
    },
    unexpect: {
      state: [ALIGNMENT_TASK_STATE.parseError, ALIGNMENT_TASK_STATE.alignError],
      msg: "文档解析失败，详细信息：",
    },
    get next() {
      return ALIGNMENT_TASK_OPTION.align;
    },
    postCheckOption: null,
  },
  align: {
    waiting: {
      state: [ALIGNMENT_TASK_STATE.aligning],
      msg: "正在自动对齐句子，请稍候...",
    },
    expect: {
      state: [ALIGNMENT_TASK_STATE.aligned, ALIGNMENT_TASK_STATE.extractDone],
      msg: "自动对齐完成",
    },
    unexpect: {
      state: [ALIGNMENT_TASK_STATE.alignError],
      msg: "自动对齐失败，详细信息：",
    },
    next: null,
    postCheckOption: {
      checker: AlignmentCellChecker,
      message: "句子对齐完成，部分数据不符合规范，详见条目内错误提示。",
    },
  },
  extract: {
    waiting: {
      state: [ALIGNMENT_TASK_STATE.extractingTerm],
      msg: "正在自动提取术语，请稍候...",
    },
    expect: {
      state: [ALIGNMENT_TASK_STATE.extractDone],
      msg: "自动提取术语完成",
    },
    unexpect: {
      state: [ALIGNMENT_TASK_STATE.extractError],
      msg: "自动提取术语失败，详细信息：",
    },
    next: null,
    postCheckOption: null,
  },
};

class AlignmentExportValidator extends ExportValidator {
  constructor(rows) {
    super();
    this.rows = rows;
  }

  validate(statistics) {
    super.validate(statistics);
    const checker = new AlignmentCellCheckerBuilder().withAllEnabled().build();
    const invalid = this.rows.some((row) => {
      return [SELECT_MODE.src, SELECT_MODE.tgt].some((column) => {
        return !checker.check(row[column]).valid;
      });
    });
    if (invalid) {
      throw new ExportValidationError(
        "部分数据不符合规范，详见条目内错误提示。"
      );
    }
  }
}

const SAVE_MODE = {
  create: 0,
  edit: 1,
};

const SELECT_MODE = {
  src: "src",
  tgt: "tgt",
  row: "row",
};

const UPLOAD_PATH = "alignment/upload";

const OPERATION = {
  insert: {
    code: 0,
    disabledMsg: "请先选中单元格或行",
    shortcut: { key: ["i"], modifier: "ctrlKey" },
  },
  delete: {
    code: 1,
    disabledMsg: "请选择需要删除的单元格或行",
    shortcut: {
      key: ["Delete"],
      modifier: null,
    },
  },
  moveup: {
    code: 2,
    disabledMsg: "请选择需要上移的单元格或行",
    shortcut: {
      key: ["ArrowUp"],
      modifier: "ctrlKey",
    },
  },
  movedown: {
    code: 3,
    disabledMsg: "请选择需要下移的单元格或行",
    shortcut: { key: ["ArrowDown"], modifier: "ctrlKey" },
  },
  swap: {
    code: 4,
    disabledMsg: "请先选中单元格或行",
    shortcut: { key: ["e"], modifier: null },
  },
  merge: {
    code: 5,
    disabledMsg: "仅支持相邻单元格或行的纵向合并",
    shortcut: { key: ["m"], modifier: "ctrlKey" },
  },
  split: {
    code: 6,
    disabledMsg: "请双击需要拆分的单元格，并使用光标指定拆分位置",
    shortcut: { key: ["Enter"], modifier: "ctrlKey" },
  },
  edit: {
    code: 7,
    disabledMsg: null,
    shortcut: null,
  },
  undo: {
    code: 8,
    disabledMsg: null,
    shortcut: { key: ["z"], modifier: "ctrlKey" },
  },
  redo: {
    code: 9,
    disabledMsg: null,
    shortcut: { key: ["y"], modifier: "ctrlKey" },
  },
  multipleUp: {
    code: 10,
    disabledMsg: null,
    shortcut: { key: ["ArrowUp"], modifier: "shiftKey" },
  },
  multipleDown: {
    code: 11,
    disabledMsg: null,
    shortcut: { key: ["ArrowDown"], modifier: "shiftKey" },
  },
};

const OPERATION_TIPS = [
  { name: "选中单元格", content: "点击单元格选中，按住Ctrl或Shift多选。" },
  { name: "选中行", content: "点击序号选中行，按住Ctrl或Shift多选。" },
  { name: "编辑", content: "双击单元格，进入文本编辑模式。" },
  {
    name: "插入",
    content: "选中单元格（行），点击插入。（Ctrl+I）",
  },
  { name: "删除", content: "选中单元格（行），点击删除。（Delete）" },
  { name: "上移", content: "选中（可多选）单元格（行），点击上移。（Ctrl+↑）" },
  { name: "下移", content: "选中（可多选）单元格（行），点击下移。（Ctrl+↓）" },
  {
    name: "对调",
    content: "选中（可多选）单元格（行），点击对调，使左右两列内容对调。（E）",
  },
  { name: "合并", content: "选中需要合并的单元格（行），点击合并。（Ctrl+M）" },
  {
    name: "拆分",
    content:
      "双击单元格，进入编辑模式，鼠标点击需要拆分的文本位置，点击拆分。（Ctrl+Enter）",
  },
  { name: "撤销", content: "Ctrl+Z。" },
  {
    name: "重做",
    content: "Ctrl+Y。",
  },
];

const MULTIPLE_SELECTION_MODIFIER = ["ctrlKey", "shiftKey"];

class KeyboardShortcutHandler {
  static SUPPRESSED_MODIFIER_FOR_SINGLE_KEY = ["ctrlKey", "altKey", "shiftKey"];

  constructor() {
    this._handler = this._handler.bind(this);
    window.addEventListener("keydown", this._handler);
    this._dealOperation = null;
    this.enabled = true;
  }

  whenOperationSent(func) {
    this._dealOperation = func;
  }

  _handler(event) {
    if (!this._dealOperation || !this.enabled) return;
    for (const op of Object.values(OPERATION)) {
      if (!op.shortcut) continue;
      const shortcut = op.shortcut;
      if (shortcut.modifier) {
        const isThisOperation =
          shortcut.key.includes(event.key) && event[shortcut.modifier];
        if (isThisOperation) {
          event.preventDefault();
          this._dealOperation(op);
          return;
        }
        continue;
      }
      if (
        shortcut.key.includes(event.key) &&
        !KeyboardShortcutHandler.SUPPRESSED_MODIFIER_FOR_SINGLE_KEY.some(
          (modifier) => event[modifier]
        )
      ) {
        this._dealOperation(op);
        return;
      }
    }
  }

  cleanup() {
    window.removeEventListener("keydown", this._handler);
  }

  enable() {
    this.enabled = true;
  }

  disable() {
    this.enabled = false;
  }
}

class AlignmentEditor {
  static OPERATION = {
    normal: 0,
    undo: 1,
    redo: 2,
  };
  constructor() {
    this.undoStack = [];
    this.redoStack = [];
    this.prevOperation = null;
  }

  // 回退或重做后的回调函数
  afterRepetition(cb) {
    this._repeatHandler = cb;
  }

  afterExecution(cb) {
    this._executionHandler = cb;
  }

  execute(command) {
    const dealtCellData = command.execute();
    this.undoStack.push(command);
    if (this.prevOperation === AlignmentEditor.OPERATION.undo) {
      this.redoStack = [];
    }
    this.prevOperation = AlignmentEditor.OPERATION.normal;
    this._executionHandler && this._executionHandler(dealtCellData);
  }

  undo() {
    const command = this.undoStack.pop();
    if (command === undefined) return;
    const dealtCellData = command.undo();
    this.redoStack.push(command);
    this.prevOperation = AlignmentEditor.OPERATION.undo;
    !!this._repeatHandler && this._repeatHandler(dealtCellData);
  }

  redo() {
    const command = this.redoStack.pop();
    if (command === undefined) return;
    const dealtCellData = command.execute();
    this.undoStack.push(command);
    this.prevOperation = AlignmentEditor.OPERATION.redo;
    !!this._repeatHandler && this._repeatHandler(dealtCellData);
  }

  clearStack() {
    this.undoStack = [];
    this.redoStack = [];
  }
}

class Command {
  constructor({ rows, columnToDeal, rowNumsToDeal }) {
    this.rows = rows;
    this.columnToDeal = columnToDeal;
    this.rowNumsToDeal = rowNumsToDeal;
  }

  get _dealtCellData() {
    return {
      rowNumsToDeal: this.rowNumsToDeal,
      columnToDeal: this.columnToDeal,
    };
  }

  execute() {
    this.executeInternally();
    return this._dealtCellData;
  }

  executeInternally() {
    throw new Error("Method 'execute()' must be implemented.");
  }

  undo() {
    this.undoInternally();
    return this._dealtCellData;
  }

  undoInternally() {
    throw new Error("Method 'undo()' must be implemented.");
  }
}

class InsertCommand extends Command {
  constructor(option) {
    super(option);
    this.rowNumToInsert = Math.min(...this.rowNumsToDeal);
  }

  executeInternally() {
    if (this.columnToDeal === SELECT_MODE.row) {
      this.rows.splice(
        this.rowNumToInsert - 1,
        0,
        RowUtil.createNewRow("", "")
      );
    } else {
      const rows = [...this.rows].reverse();
      const lastRowBeforeInsert = { ...rows[0] };
      const lastRow = {};
      const columnKey = this.columnToDeal;
      rows.forEach((row, i) => {
        const rowNum = rows.length - i;
        if (rowNum < this.rowNumToInsert) return;
        if (rowNum === this.rowNumToInsert) {
          row[columnKey] = "";
          return;
        }
        row[columnKey] = rows[i + 1][columnKey];
      });
      const lastCellText = lastRowBeforeInsert[columnKey];
      lastRow[columnKey] = lastCellText;
      [lastRow.src, lastRow.tgt].filter(Boolean).length > 0 &&
        this.rows.push(lastRow);
    }
  }

  undoInternally() {
    if (this.columnToDeal === SELECT_MODE.row) {
      this.rows.splice(this.rowNumToInsert - 1, 1);
    } else {
      const columnKey = this.columnToDeal;
      this.rows.forEach((row, i) => {
        if (i + 1 < this.rowNumToInsert) return;
        if (i < this.rows.length - 1) {
          row[columnKey] = this.rows[i + 1][columnKey];
          return;
        }
        row[columnKey] = "";
        RowUtil.isRowEmpty(row) && this.rows.splice(i, 1);
      });
    }
  }
}

class DeleteCommand extends Command {
  constructor(option) {
    super(option);
    this.rowNumsToDelete = this.rowNumsToDeal;
    this.firstRowNumToDelete = Math.min(...this.rowNumsToDelete);
    this.step = this.rowNumsToDelete.length;
    this.deletedData = [];
  }

  executeInternally() {
    if (this.columnToDeal === SELECT_MODE.row) {
      this.deletedData = this.rows.splice(
        this.firstRowNumToDelete - 1,
        this.step
      );
    } else {
      const columnKey = this.columnToDeal;
      this.rows.forEach((row, i) => {
        if (i + 1 < this.firstRowNumToDelete) return;
        if (i === this.rows.length - 1) {
          this.deletedData.push({ ...row });
          row[columnKey] = "";
          return;
        }
        this.rowNumsToDelete.includes(i + 1) &&
          this.deletedData.push({ ...row });
        if (i + 1 <= this.rows.length - this.step) {
          row[columnKey] = this.rows[i + this.step][columnKey];
          return;
        }
        row[columnKey] = "";
      });
      RowUtil.cleanTrailingEmptyRows(this.rows);
    }
  }

  undoInternally() {
    if (this.columnToDeal === SELECT_MODE.row) {
      this.rows.splice(this.firstRowNumToDelete - 1, 0, ...this.deletedData);
    } else {
      const columnKey = this.columnToDeal;
      const rows = [...this.rows].reverse();
      const lastRowBeforeInsert = { ...rows[0] };
      const lastRow = {};
      const firstDeletedRowNum = this.firstRowNumToDelete;
      rows.forEach((row, i) => {
        const rowNum = rows.length - i;
        if (rowNum < firstDeletedRowNum) return;
        if (rowNum < firstDeletedRowNum + this.step) {
          row[columnKey] = this.deletedData.find((item) => item.id === row.id)[
            columnKey
          ];
          return;
        }
        row[columnKey] = rows[i + this.step][columnKey];
      });
      const lastCellText = lastRowBeforeInsert[columnKey];
      lastRow[columnKey] = lastCellText;
      [lastRow.src, lastRow.tgt].filter(Boolean).length > 0 &&
        this.rows.push(lastRow);
    }
  }
}

class MoveCommand extends Command {
  constructor(option) {
    super(option);
    const rowNumsToMove = this.rowNumsToDeal;
    this.step = rowNumsToMove.length;
    this.firstRowNumToMove = Math.min(...rowNumsToMove);
    this.movedData = this.rows.slice(
      this.firstRowNumToMove - 1,
      this.firstRowNumToMove - 1 + this.step
    );
  }

  moveup() {
    const movedUpData = this.movedData;
    const firstMovedRow = movedUpData[0];
    const firstRowIndexToMove = RowUtil.findIndex(this.rows, firstMovedRow);
    if (this.columnToDeal != SELECT_MODE.row) {
      const prevRow = this.rows[firstRowIndexToMove - 1];
      const columnKey = this.columnToDeal;
      const firstRowColumnText = firstMovedRow[columnKey];
      const prevRowColumnText = prevRow[columnKey];
      movedUpData.forEach((movedRow, i) => {
        if (i === movedUpData.length - 1) {
          movedRow[columnKey] = prevRowColumnText;
          return;
        }
        const index = RowUtil.findIndex(this.rows, movedRow);
        movedRow[columnKey] = this.rows[index + 1][columnKey];
      });
      prevRow[columnKey] = firstRowColumnText;
      return;
    }
    this.rows.splice(firstRowIndexToMove, this.step);
    this.rows.splice(firstRowIndexToMove - 1, 0, ...movedUpData);
  }

  movedown() {
    const movedData = this.movedData;
    const lastMovedDownRow = movedData[this.step - 1];
    const lastMovedDownIndex = RowUtil.findIndex(this.rows, lastMovedDownRow);
    const nextRowIndex = lastMovedDownIndex + 1;
    if (this.columnToDeal !== SELECT_MODE.row) {
      const nextRow = this.rows[nextRowIndex];
      const columnKey = this.columnToDeal;
      const nextRowColumnText = nextRow[columnKey];
      const lastRowColumnText = lastMovedDownRow[columnKey];
      const reversed = [...movedData].reverse();
      reversed.forEach((movedRow, i) => {
        if (i === reversed.length - 1) {
          movedRow[columnKey] = nextRowColumnText;
          return;
        }
        const index = RowUtil.findIndex(this.rows, movedRow);
        movedRow[columnKey] = this.rows[index - 1][columnKey];
      });
      nextRow[columnKey] = lastRowColumnText;
      return;
    }
    const movedUpData = this.rows.splice(nextRowIndex, 1);
    this.rows.splice(nextRowIndex - this.step, 0, ...movedUpData);
  }

  resetMovedData(offset) {
    if (this.columnToDeal !== SELECT_MODE.row) {
      this.movedData = this.movedData.map(
        (row) => this.rows[RowUtil.findIndex(this.rows, row) + offset]
      );
    } else {
      this.movedData = this.movedData.map(
        (row) => this.rows[RowUtil.findIndex(this.rows, row)]
      );
    }
  }
}

class MoveUpCommand extends MoveCommand {
  constructor(option) {
    super(option);
  }

  executeInternally() {
    return this.moveup();
  }

  undoInternally() {
    this.resetMovedData(-1);
    return this.movedown();
  }
}

class MoveDownCommand extends MoveCommand {
  constructor(option) {
    super(option);
  }

  executeInternally() {
    return this.movedown();
  }

  undoInternally() {
    this.resetMovedData(1);
    return this.moveup();
  }
}

class SwapCommand extends Command {
  constructor(option) {
    super(option);
    this.rowNumsToSwap = this.rowNumsToDeal;
  }

  executeInternally() {
    this.swap();
  }

  undoInternally() {
    this.swap();
  }

  swap() {
    this.rowNumsToSwap.forEach((num) => {
      const curRow = this.rows[num - 1];
      [curRow["src"], curRow["tgt"]] = [curRow["tgt"], curRow["src"]];
    });
  }
}

class MergeCommand extends Command {
  constructor(option) {
    super(option);
    this.rowNumsToMerge = this.rowNumsToDeal;
    this.firstRowNum = Math.min(...this.rowNumsToMerge);
    this.step = this.rowNumsToMerge.length;
    this.deletedData = [];
  }

  executeInternally() {
    if (this.columnToDeal === SELECT_MODE.row) {
      this.deletedData = this.rows.splice(this.firstRowNum - 1, this.step);
      const mergedRow = this.deletedData.reduce(
        (acc, cur) => {
          acc.src += cur.src;
          acc.tgt += cur.tgt;
          return acc;
        },
        {
          src: "",
          tgt: "",
        }
      );
      this.rows.splice(this.firstRowNum - 1, 0, mergedRow);
    } else {
      const columnKey = this.columnToDeal;
      const anotherColumnKey = RowUtil.getAnotherColumnKey(columnKey);
      this.deletedData = cloneDeep(
        this.rows.slice(this.firstRowNum - 1, this.firstRowNum - 1 + this.step)
      );
      const mergedRow = this.deletedData.reduce(
        (acc, cur) => {
          acc[columnKey] += cur[columnKey];
          return acc;
        },
        {
          [columnKey]: "",
          [anotherColumnKey]: this.deletedData[0][anotherColumnKey],
        }
      );
      const rowToReplace = this.rows[this.firstRowNum - 1];
      RowUtil.updateRowProp(mergedRow, rowToReplace);
      this.rows.forEach((row, i) => {
        if (i + 1 <= this.firstRowNum) return;
        if (i === this.rows.length - 1) {
          row[columnKey] = "";
          return;
        }
        if (i + 1 <= this.rows.length - this.step + 1) {
          row[columnKey] = this.rows[i + this.step - 1][columnKey];
          return;
        }
        row[columnKey] = "";
      });
      RowUtil.cleanTrailingEmptyRows(this.rows);
    }
  }

  undoInternally() {
    if (this.columnToDeal === SELECT_MODE.row) {
      this.rows.splice(this.firstRowNum - 1, 1);
      this.rows.splice(this.firstRowNum - 1, 0, ...this.deletedData);
    } else {
      const columnKey = this.columnToDeal;
      const rows = [...this.rows].reverse();
      const extraTexts = this.findTrailingContinousTexts(rows);
      const targets = RowUtil.slicePaddedRows(
        this.rows,
        this.firstRowNum - 1,
        this.firstRowNum - 1 + this.step
      );
      RowUtil.updateRowsProp(this.deletedData, targets);
      if (extraTexts.length > 0) {
        // get trailing rows
        const trailingRows = this.rows.slice(this.firstRowNum + this.step - 1);
        trailingRows.forEach((row) => {
          // fill column
          const text = extraTexts.shift();
          row[columnKey] = text;
        });
        // push row if texts still remains
        extraTexts.length > 0 &&
          extraTexts.forEach((text) => {
            this.rows.push({
              [columnKey]: text,
              [RowUtil.getAnotherColumnKey(columnKey)]: "",
            });
          });
      }
    }
  }

  findTrailingContinousTexts(rows) {
    const result = [];
    for (let i = 0; i < rows.length - this.firstRowNum; i++) {
      const text = rows[i][this.columnToDeal];
      if (isBlank(text)) continue;
      result.push(text);
    }
    return result.reverse();
  }
}

class SplitCommand extends Command {
  constructor(option) {
    super(option);
    this.rowNumToSplit = Math.min(...this.rowNumsToDeal);
    this.cursorPos = option.cursorPos;
    this.columnKey = this.columnToDeal;
    this.originalRow = {};
  }

  executeInternally() {
    const rowToSplit = this.rows.slice(
      this.rowNumToSplit - 1,
      this.rowNumToSplit
    )[0];
    this.originalRow = { ...rowToSplit };
    const textToSplit = this.originalRow[this.columnKey];
    const splittedUpText = textToSplit.substring(0, this.cursorPos);
    const splittedDownText = textToSplit.substring(this.cursorPos);
    rowToSplit[this.columnKey] = splittedUpText;
    if (this.rowNumToSplit === this.rows.length)
      this.rows.push(RowUtil.createNewRow("", ""));
    const rows = [...this.rows].reverse();
    const lastCellText = rows[0][this.columnKey];
    rows.forEach((row, i) => {
      const index = rows.length - i - 1;
      if (index + 1 <= this.rowNumToSplit) return;
      if (index + 1 === this.rowNumToSplit + 1) {
        row[this.columnKey] = splittedDownText;
        return;
      }
      row[this.columnKey] = rows[i + 1][this.columnKey];
    });
    const lastRow = {
      [this.columnKey]: lastCellText,
      [RowUtil.getAnotherColumnKey(this.columnKey)]: "",
    };
    RowUtil.isRowEmpty(lastRow) || this.rows.push(lastRow);
  }

  undoInternally() {
    this.rows.splice(this.rowNumToSplit - 1, 1, this.originalRow);
    this.rows.forEach((row, i) => {
      if (i + 1 <= this.rowNumToSplit) return;
      if (i === this.rows.length - 1) {
        row[this.columnKey] = "";
        RowUtil.isRowEmpty(row) && this.rows.splice(i, 1);
        return;
      }
      row[this.columnKey] = this.rows[i + 1][this.columnKey];
    });
  }
}

class EditCommand extends Command {
  constructor(option) {
    super(option);
    this.editRowNum = this.rowNumsToDeal[0];
    this.editBefore = option.editBefore;
    this.editAfter = option.editAfter;
  }

  executeInternally() {
    this.rows[this.editRowNum - 1][this.columnToDeal] = this.editAfter;
  }

  undoInternally() {
    this.rows[this.editRowNum - 1][this.columnToDeal] = this.editBefore;
  }
}

class RowUtil {
  static slicePaddedRows(rows, startIndex, endIndex) {
    const lastRowIndex = rows.length - 1;
    const extraRowNums = endIndex - lastRowIndex - 1;
    if (extraRowNums > 0) {
      for (let i = lastRowIndex + 1; i < endIndex; i++) {
        rows.splice(i, 0, RowUtil.createNewRow("", ""));
      }
    }
    return rows.slice(startIndex, endIndex);
  }

  static cleanTrailingEmptyRows(rows) {
    for (let i = rows.length - 1; i < rows.length; i--) {
      if (!RowUtil.isRowEmpty(rows[i])) break;
      rows.splice(i, 1);
    }
  }

  static isRowEmpty(row) {
    return (
      ["src", "tgt"].map((key) => row[key]).filter((text) => !isBlank(text))
        .length === 0
    );
  }

  static getAnotherColumnKey(prop) {
    if (SELECT_MODE.src === prop) return SELECT_MODE.tgt;
    if (SELECT_MODE.tgt === prop) return SELECT_MODE.src;
  }

  static createNewRow(src, tgt) {
    return {
      src,
      tgt,
    };
  }

  static findIndex(rows, row) {
    return rows.indexOf(row);
  }

  static updateRowProp(source, target) {
    Object.keys(source).forEach((key) => {
      target[key] = source[key];
    });
  }

  static updateRowsProp(sources, targets) {
    sources.forEach((source, i) => {
      RowUtil.updateRowProp(source, targets[i]);
    });
  }
}

class Paginator {
  // 分页会多出的上下文句数
  static CONTEXT_NUM = 3;

  constructor(rows) {
    this.rows = rows;
  }

  load(pageIndex, pageSize) {
    this.rangeStart = pageSize * (pageIndex - 1);
    this.rangeMaxEnd = this.rangeStart + pageSize + Paginator.CONTEXT_NUM;
    return this.rows.slice(this.rangeStart, this.rangeMaxEnd);
  }

  getCurrentPageRowNum(realRowNum) {
    return realRowNum - this.rangeStart;
  }

  getRealRowNum(rowNum) {
    return this.rangeStart + rowNum;
  }

  getBelongedPage(realRowNum, pageSize) {
    const total = Math.ceil(this.rows.length / pageSize);
    for (let pageIndex = 1; pageIndex <= total; pageIndex++) {
      const rangeStart = pageSize * (pageIndex - 1);
      const rangeMaxEnd = rangeStart + pageSize + Paginator.CONTEXT_NUM;
      if (realRowNum <= rangeMaxEnd) return pageIndex;
    }
  }

  isRealLastRow(realRowNum) {
    return realRowNum === this.rows.length;
  }

  isCurrentPageLastRow(realRowNum) {
    return realRowNum === this.rangeMaxEnd;
  }

  isCurrentPageFirstRow(realRowNum) {
    return realRowNum === this.rangeStart + 1;
  }

  isRowRangeInCurrentPage(realRowNums) {
    return realRowNums.every((rowNum) => this._isRowNumInCurrentPage(rowNum));
  }

  _isRowNumInCurrentPage(realRowNum) {
    return this.rangeStart < realRowNum && realRowNum <= this.rangeMaxEnd;
  }

  indexOf(row) {
    return this.rows.indexOf(row);
  }
}

class AlignmentPollApiHandler {
  static async execute(api) {
    let response;
    try {
      response = await api();
    } catch (error) {
      return {
        success: false,
        data: null,
        modalOption: {
          buttonText: "重试",
          message: error,
          type: MODAL_TYPE.retry,
        },
      };
    }
    if (response.data.success)
      return {
        success: true,
        data: response.data.data,
        modalOption: null,
      };
    if (response.data.code === SYSTEM_RESPONSE_CODE.notFound) {
      return {
        success: false,
        data: null,
        modalOption: {
          buttonText: "确定",
          message: response.data.error,
          type: MODAL_TYPE.confirm,
        },
      };
    }
    return {
      success: false,
      data: null,
      modalOption: {
        buttonText: "重试",
        message: response.data.error,
        type: MODAL_TYPE.retry,
      },
    };
  }
}

const MODAL_TYPE = {
  confirm: 0,
  retry: 1,
};

const MODAL_ACTION = {
  confirm: 0,
  cancel: 1,
  notShown: 2,
  closeTab: 3,
};

const exportOptions = {
  tm: {
    title: "记忆库",
    formats: TM_EXPORT_OPTIONS,
    validateOption: {
      trigger: "hover", // 导出按钮悬浮
      checkExportable({ state }) {
        return (
          state === ALIGNMENT_TASK_STATE.aligned ||
          state >= ALIGNMENT_TASK_STATE.extractingTerm
        );
      },
      disabledTip: "句对齐完成后才能导出",
    },
  },
  tb: {
    title: "术语库",
    formats: TB_EXPORT_OPTIONS,
    validateOption: {
      trigger: "click", // 下拉菜单
      checkExportable({ state }) {
        return state === ALIGNMENT_TASK_STATE.extractDone;
      },
      disabledTip: "术语提取完成后才能导出",
    },
  },
};

export {
  ALIGNMENT_TASK_STATE,
  ALIGNMENT_TASK_ERROR,
  ALIGNMENT_TASK_NORMAL,
  ALIGNMENT_TASK_OPTION,
  SAVE_MODE,
  SELECT_MODE,
  UPLOAD_PATH,
  OPERATION,
  STAGE_NORMAL_DESC,
  STAGE_ERROR_DESC,
  STAGE_TIP_DATA,
  LAST_STAGE_TIP_DATA,
  MULTIPLE_SELECTION_MODIFIER,
  OPERATION_TIPS,
  MODAL_TYPE,
  MODAL_ACTION,
  AlignmentPollApiHandler,
  AlignmentEditor,
  InsertCommand,
  DeleteCommand,
  MoveUpCommand,
  MoveDownCommand,
  SwapCommand,
  MergeCommand,
  SplitCommand,
  EditCommand,
  RowUtil,
  KeyboardShortcutHandler,
  Paginator,
  AlignmentCellChecker,
  AlignmentExportValidator,
  AlignmentCellCheckerBuilder,
  exportOptions,
};
